import { Link } from 'react-router-dom';
import {
  Construction,
  Satellite,
  MapPin,
  Wifi,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function Telematics() {
  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Telematics</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Live vehicle tracking — current GPS positions, real-time geofence violations,
          and driver-behaviour scores.
        </p>
      </div>

      <Card className="border-amber-200 bg-amber-50/40">
        <CardHeader className="flex flex-row items-center gap-2 pb-2">
          <Construction className="h-4 w-4 text-amber-600" />
          <CardTitle className="text-sm">Awaiting data feed</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          A live GPS ingestion pipeline has not yet been configured — no telematics
          provider is feeding us positions, and the WebSocket stream off
          <code className="mx-1 rounded bg-muted px-1 py-0.5 text-xs">
            fleet.vehicle_movement
          </code>
          will only carry traffic once that source lands. The schema is ready;
          the source is not.
        </CardContent>
      </Card>

      <div>
        <h2 className="text-base font-semibold">Available today</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Partial signals you can use right now while the dedicated dashboard is wired up.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-2">
            <Satellite className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Movement schema is live</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              The{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                fleet.vehicle_movement
              </code>{' '}
              table exists with the expected columns and PostGIS index — it is just
              empty. As soon as a provider posts positions, this dashboard can light
              up without further migrations.
            </p>
            <a
              href="/api/v1/docs"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
            >
              Browse fleet endpoints in the API docs
              <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Station coordinates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                fleet.station.geog
              </code>{' '}
              is fully populated and already powers the merchant-network map — the
              same coordinates will anchor geofence checks once vehicles broadcast
              positions.
            </p>
            <a
              href="/api/v1/docs#/Merchants"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
            >
              GET /api/v1/merchants/ops
              <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-2">
            <Wifi className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Impossible-travel alerts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              When movement data lands, impossible-travel and geofence-violation
              alerts will surface in the Fraud Center under{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                category_code='travel'
              </code>
              .
            </p>
            <Link
              to="/fraud?category=travel"
              className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
            >
              Open Fraud Center filtered by category_code=travel
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-2">
            <Construction className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">What unblocks this dashboard</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            A telematics provider integration (MiX, Cartrack, Geotab, or equivalent)
            posting per-vehicle GPS pings into{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              fleet.vehicle_movement
            </code>
            , plus a WebSocket fan-out off the same table for the live map.
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
